/**
 * EditorialPrimitives.jsx — GRANULLO_UNIVERSAL_EDITORIAL_SYSTEM_V1
 *
 * Universal design primitives enforced platform-wide.
 * No page may implement its own expansion glyph, numbering, or open-state treatment.
 *
 * Exports:
 *   - SignatureExpansionGlyph: + → × with hand-drawn circle (canonical expansion)
 *   - EditorialSectionNumber: Permanent Marker numbered sections (01. 02. 03.)
 *   - EditorialCircledClose: Standalone circled × for close states
 */
import React from 'react';
import { ExpandToggle } from '../ui/ExpandToggle';

const EDITORIAL_RED = '#CD2200';

/**
 * SignatureExpansionGlyph V3 — now a thin wrapper over the canonical
 * <ExpandToggle /> component. This preserves every existing consumer's API
 * while migrating them to the FAQ-style hand-sketched "+" + scribble circle
 * in one shot.
 *
 * Closed: hand-sketched editorial red "+" (Permanent Marker font)
 * Open:   "+" rotates to "×" and a scarlet scribble circle draws itself in
 */
export function SignatureExpansionGlyph({ open, size = 14, color = EDITORIAL_RED }) {
  // Map legacy numeric size → canonical toggle size keyword
  const toggleSize = size <= 12 ? 'xs' : size <= 16 ? 'sm' : size <= 26 ? 'md' : 'lg';
  // If a non-scarlet color was passed, render the neutral tone
  const tone = color && color.toLowerCase() !== EDITORIAL_RED.toLowerCase() ? 'neutral' : 'scarlet';
  return <ExpandToggle open={!!open} size={toggleSize} tone={tone} testId="signature-expansion-glyph" />;
}

/**
 * EditorialSectionNumber — Permanent Marker handwritten section numbering.
 *
 * Renders: "01." "02." etc. in editorial red with Permanent Marker font.
 * Consistent across all numbered modular content blocks.
 */
export function EditorialSectionNumber({ number, size = 'md' }) {
  const sizeMap = {
    sm: { fontSize: '13px', lineHeight: '1' },
    md: { fontSize: '16px', lineHeight: '1' },
    lg: { fontSize: '22px', lineHeight: '1' },
  };
  const styles = sizeMap[size] || sizeMap.md;

  return (
    <span
      className="inline-block font-bold mr-2 select-none"
      style={{
        fontFamily: '"Permanent Marker", cursive',
        color: EDITORIAL_RED,
        fontSize: styles.fontSize,
        lineHeight: styles.lineHeight,
        letterSpacing: '0.02em',
        transform: 'rotate(-1.5deg)',
        display: 'inline-block',
      }}
      data-testid={`editorial-section-number-${number}`}
    >
      {String(number).padStart(2, '0')}.
    </span>
  );
}

/**
 * EditorialCircledClose — Standalone hand-drawn circled × for close buttons.
 * For use in modals, panels, overlays.
 */
export function EditorialCircledClose({ size = 20, color = EDITORIAL_RED, onClick }) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.4;
  const strokeW = size > 24 ? 1.8 : 1.4;

  return (
    <button
      onClick={onClick}
      className="inline-flex items-center justify-center"
      style={{ width: size, height: size, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
      data-testid="editorial-circled-close"
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={strokeW * 0.8} strokeLinecap="round" opacity="0.6" />
        <line x1={size * 0.3} y1={size * 0.3} x2={size * 0.7} y2={size * 0.7} stroke={color} strokeWidth={strokeW} strokeLinecap="round" />
        <line x1={size * 0.7} y1={size * 0.3} x2={size * 0.3} y2={size * 0.7} stroke={color} strokeWidth={strokeW} strokeLinecap="round" />
      </svg>
    </button>
  );
}
